import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { refreshPush, disablePush } from './pushWeb';
import Navbar from './components/Navbar';
import Home from './components/Home';
import Login from './components/Login';
import Register from './components/Register';
import Mora from './components/Mora';
import Companies from './components/Companies';
import Users from './components/Users';
import Channels from './components/Channels';
import ContactLists from './components/ContactLists';
import ContactListView from './components/ContactListView';
import CustomFields from './components/CustomFields';
import Typifications from './components/Typifications';
import Archives from './components/Archives';
import Flows from './components/Flows';
import FlowBuilder from './components/FlowBuilder';
import Campaigns from './components/Campaigns';
import CampaignEdit from './components/CampaignEdit';
import HabeasModal from './components/HabeasModal';
import AgentView from './components/agent/AgentView';
import SaldoWidget from './components/SaldoWidget';
import NotificationModal from './components/NotificationModal';
import ConversationsAdmin from './components/ConversationsAdmin';
import AdminNotifier from './components/AdminNotifier';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Templates from './components/Templates';
import TemplateBuilder from './components/TemplateBuilder';
import Marketing from './components/Marketing';
import MarketingLogs from './components/MarketingLogs';
import RecargarResultado from './components/RecargarResultado';
import EmailTemplates from './components/EmailTemplates';
import EmailTemplateEditor from './components/EmailTemplateEditor';
import PdfTemplates from './components/PdfTemplates';
import PdfTemplateEditor from './components/PdfTemplateEditor';
import Empresa from './components/Empresa';
import HorarioConfig from './components/HorarioConfig';
import PagoResultado from './components/PagoResultado';
import TransaccionesClientes from './components/TransaccionesClientes';
import Products from './components/Products';
import Forms from './components/Forms';
import FormBuilder from './components/FormBuilder';
import Services from './components/Services';
import Citas from './components/Citas';
import Pedidos from './components/Pedidos';
import ConversationViewer from './components/ConversationViewer';
import Privacidad from './components/legal/Privacidad';
import Terminos from './components/legal/Terminos';
import EliminacionDatos from './components/legal/EliminacionDatos';
import Storefront from './components/Storefront';
import PublicCita from './components/PublicCita';
import Tickets from './components/Tickets';
import PublicTicket from './components/PublicTicket';
import Noticias from './components/Noticias';
import PublicNews from './components/PublicNews';
import PublicPortal from './components/PublicPortal';
import Vitrina from './components/Vitrina';
import WebChatPage from './components/web/WebChatPage';
import MisLinks from './components/MisLinks';
import CajeroConfig from './components/cajero/CajeroConfig';
import Cajero from './components/cajero/Cajero';
import Tutoriales from './components/Tutoriales';
import Tarifas from './components/Tarifas';
import { theme } from './theme';

function App() {
  const [user, setUser] = useState(null);
  const [companyBlocked, setCompanyBlocked] = useState(false);
  const [companySaldo, setCompanySaldo] = useState(null);
  const [roleId, setRoleId] = useState(null);
  const [perUserCalendar, setPerUserCalendar] = useState(false);
  const [habeasPending, setHabeasPending] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const username = localStorage.getItem('username');
    if (token && username) {
      fetch('/api/auth/me', {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (data) {
            setUser(username);
            setRoleId(data.role_id);
            setPerUserCalendar(!!data.per_user_calendar);
            setCompanyBlocked(!!data.company_blocked);
            setCompanySaldo(data.company_saldo || null);
            setHabeasPending(!!data.habeas_pending);
            localStorage.setItem('company_blocked', data.company_blocked ? 'true' : 'false');
            localStorage.setItem('company_saldo', data.company_saldo ? JSON.stringify(data.company_saldo) : '');
            // Firebase rota el token del dispositivo cada cierto tiempo, así que hay
            // que reenviarlo en cada arranque para que el servidor siga sabiendo a
            // qué celular avisar. Es idempotente y no pide permiso, así que no
            // necesita un gesto del usuario. Si no hay permiso concedido, no hace nada.
            refreshPush(token);
          } else {
            handleLogout();
          }
        })
        .catch(() => handleLogout())
        .finally(() => setAuthLoading(false));
    } else {
      setAuthLoading(false);
    }
  }, []);

  const handleLogout = () => {
    // Da de baja este dispositivo antes de borrar el token, porque hace falta para
    // autenticar la baja contra /api/devices. Sin esto el celular seguiría recibiendo
    // los avisos de una cuenta que ya cerró sesión en él.
    disablePush(localStorage.getItem('token'));
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    localStorage.removeItem('role_id');
    localStorage.removeItem('company_id');
    localStorage.removeItem('company_blocked');
    localStorage.removeItem('company_saldo');
    setUser(null);
    setCompanyBlocked(false);
    setCompanySaldo(null);
    setRoleId(null);
    setHabeasPending(false);
  };

  const token = localStorage.getItem('token') || '';

  const showWidget = user && [1, 2, 3].includes(roleId)
    && !window.location.pathname.startsWith('/p/')
    && !window.location.pathname.startsWith('/tienda/')
    && !window.location.pathname.startsWith('/cita/');

  const renderContent = () => {
    if (authLoading) return null;
    if (user && habeasPending && window.location.pathname !== '/recargar/resultado') {
      return (
        <HabeasModal
          token={token}
          onAccept={() => setHabeasPending(false)}
          onReject={handleLogout}
        />
      );
    }
    if (user && companyBlocked && roleId !== 1 && window.location.pathname !== '/recargar/resultado') {
      return <Mora username={user} onLogout={handleLogout} />;
    }
    return (
      <Routes>
        <Route
          path="/"
          element={
            user && roleId === 4
              ? <Navigate to="/agent" replace />
              : <Home user={user} token={token} roleId={roleId} />
          }
        />
        <Route
          path="/agent"
          element={
            user && roleId === 4
              ? <AgentView token={token} onLogout={handleLogout} />
              : <Navigate to="/" replace />
          }
        />
        <Route
          path="/login"
          element={
            <Login
              setUser={setUser}
              setCompanyBlocked={setCompanyBlocked}
              setCompanySaldo={setCompanySaldo}
              setRoleId={setRoleId}
              setPerUserCalendar={setPerUserCalendar}
              setHabeasPending={setHabeasPending}
              setNotifications={setNotifications}
            />
          }
        />
        <Route path="/register" element={<Register />} />
        <Route path="/vitrina-productos" element={<Vitrina tipo="productos" />} />
        <Route path="/vitrina-servicios" element={<Vitrina tipo="servicios" />} />
        <Route path="/tutoriales" element={<Tutoriales />} />
        <Route path="/tarifas" element={<Tarifas />} />
        <Route path="/privacidad" element={<Privacidad />} />
        <Route path="/terminos" element={<Terminos />} />
        <Route path="/eliminacion-datos" element={<EliminacionDatos />} />
        <Route path="/recargar/resultado" element={<RecargarResultado />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route
          path="/companies"
          element={
            user && roleId === 1
              ? <Companies token={token} />
              : <Navigate to="/" replace />
          }
        />
        <Route
          path="/users"
          element={
            user && (roleId === 1 || roleId === 2)
              ? <Users token={token} />
              : <Navigate to="/" replace />
          }
        />
        <Route
          path="/channels"
          element={
            user && (roleId === 1 || roleId === 2 || roleId === 3)
              ? <Channels token={token} />
              : <Navigate to="/" replace />
          }
        />
        <Route
          path="/listas-contactos"
          element={
            user && (roleId === 1 || roleId === 2 || roleId === 3)
              ? <ContactLists token={token} />
              : <Navigate to="/" replace />
          }
        />
        <Route
          path="/listas-contactos/:id"
          element={
            user && (roleId === 1 || roleId === 2 || roleId === 3)
              ? <ContactListView token={token} />
              : <Navigate to="/" replace />
          }
        />
        <Route
          path="/campos-personalizados"
          element={
            user && (roleId === 1 || roleId === 2 || roleId === 3)
              ? <CustomFields token={token} />
              : <Navigate to="/" replace />
          }
        />
        <Route
          path="/tipificaciones"
          element={
            user && (roleId === 1 || roleId === 2 || roleId === 3)
              ? <Typifications token={token} />
              : <Navigate to="/" replace />
          }
        />
        <Route
          path="/archivos"
          element={
            user && (roleId === 1 || roleId === 2 || roleId === 3)
              ? <Archives token={token} />
              : <Navigate to="/" replace />
          }
        />
        <Route
          path="/flows"
          element={
            user && (roleId === 1 || roleId === 2 || roleId === 3)
              ? <Flows token={token} />
              : <Navigate to="/" replace />
          }
        />
        <Route
          path="/flows/:id/edit"
          element={
            user && (roleId === 1 || roleId === 2 || roleId === 3)
              ? <FlowBuilder token={token} />
              : <Navigate to="/" replace />
          }
        />
        <Route
          path="/campaigns"
          element={
            user && (roleId === 1 || roleId === 2 || roleId === 3)
              ? <Campaigns token={token} />
              : <Navigate to="/" replace />
          }
        />
        <Route
          path="/campaigns/:id/edit"
          element={
            user && (roleId === 1 || roleId === 2 || roleId === 3)
              ? <CampaignEdit token={token} />
              : <Navigate to="/" replace />
          }
        />
        <Route
          path="/conversaciones"
          element={
            user && (roleId === 1 || roleId === 2 || roleId === 3)
              ? <ConversationsAdmin token={token} roleId={roleId} />
              : <Navigate to="/" replace />
          }
        />
        <Route
          path="/plantillas"
          element={
            user && (roleId === 1 || roleId === 2 || roleId === 3)
              ? <Templates token={token} roleId={roleId} />
              : <Navigate to="/" replace />
          }
        />
        <Route
          path="/plantillas/nueva"
          element={
            user && (roleId === 1 || roleId === 2 || roleId === 3)
              ? <TemplateBuilder token={token} roleId={roleId} />
              : <Navigate to="/" replace />
          }
        />
        <Route
          path="/publicidad"
          element={
            user && (roleId === 1 || roleId === 2 || roleId === 3)
              ? <Marketing token={token} roleId={roleId} companySaldo={companySaldo} />
              : <Navigate to="/" replace />
          }
        />
        <Route
          path="/publicidad/:id"
          element={
            user && (roleId === 1 || roleId === 2 || roleId === 3)
              ? <MarketingLogs token={token} />
              : <Navigate to="/" replace />
          }
        />
        <Route
          path="/plantillas-email"
          element={
            user && (roleId === 1 || roleId === 2 || roleId === 3)
              ? <EmailTemplates token={token} roleId={roleId} />
              : <Navigate to="/" replace />
          }
        />
        <Route
          path="/plantillas-email/nueva"
          element={
            user && (roleId === 1 || roleId === 2 || roleId === 3)
              ? <EmailTemplateEditor token={token} />
              : <Navigate to="/" replace />
          }
        />
        <Route
          path="/plantillas-email/:id/editar"
          element={
            user && (roleId === 1 || roleId === 2 || roleId === 3)
              ? <EmailTemplateEditor token={token} />
              : <Navigate to="/" replace />
          }
        />
        <Route
          path="/plantillas-pdf"
          element={
            user && (roleId === 1 || roleId === 2 || roleId === 3)
              ? <PdfTemplates token={token} roleId={roleId} />
              : <Navigate to="/" replace />
          }
        />
        <Route
          path="/plantillas-pdf/nueva"
          element={
            user && (roleId === 1 || roleId === 2 || roleId === 3)
              ? <PdfTemplateEditor token={token} />
              : <Navigate to="/" replace />
          }
        />
        <Route
          path="/plantillas-pdf/:id/editar"
          element={
            user && (roleId === 1 || roleId === 2 || roleId === 3)
              ? <PdfTemplateEditor token={token} />
              : <Navigate to="/" replace />
          }
        />
        <Route
          path="/empresa"
          element={
            user && (roleId === 1 || roleId === 2)
              ? <Empresa token={token} roleId={roleId} />
              : <Navigate to="/" replace />
          }
        />
        <Route
          path="/configuracion-horario"
          element={
            user && (roleId === 2 || roleId === 3)
              ? <HorarioConfig token={token} />
              : <Navigate to="/" replace />
          }
        />
        <Route
          path="/transacciones-clientes"
          element={
            user && (roleId === 2 || roleId === 3)
              ? <TransaccionesClientes token={token} />
              : <Navigate to="/" replace />
          }
        />
        <Route
          path="/productos"
          element={
            user && (roleId === 1 || roleId === 2 || roleId === 3)
              ? <Products token={token} />
              : <Navigate to="/" replace />
          }
        />
        <Route
          path="/formularios"
          element={
            user && (roleId === 1 || roleId === 2 || roleId === 3)
              ? <Forms token={token} />
              : <Navigate to="/" replace />
          }
        />
        <Route
          path="/formularios/:id/editor"
          element={
            user && (roleId === 1 || roleId === 2 || roleId === 3)
              ? <FormBuilder token={token} />
              : <Navigate to="/" replace />
          }
        />
        <Route
          path="/servicios"
          element={
            user && (roleId === 1 || roleId === 2 || roleId === 3)
              ? <Services token={token} />
              : <Navigate to="/" replace />
          }
        />
        <Route
          path="/citas"
          element={
            user && (roleId === 1 || roleId === 2 || roleId === 3)
              ? <Citas token={token} />
              : <Navigate to="/" replace />
          }
        />
        <Route
          path="/pedidos"
          element={
            user && (roleId === 1 || roleId === 2 || roleId === 3)
              ? <Pedidos token={token} />
              : <Navigate to="/" replace />
          }
        />
        <Route
          path="/cajero-config"
          element={
            user && (roleId === 1 || roleId === 2)
              ? <CajeroConfig token={token} />
              : <Navigate to="/" replace />
          }
        />
        <Route
          path="/cajero"
          element={
            user && (roleId === 1 || roleId === 2 || roleId === 3)
              ? <Cajero token={token} />
              : <Navigate to="/" replace />
          }
        />
        <Route
          path="/mis-links"
          element={
            user && (roleId === 1 || roleId === 2 || roleId === 3)
              ? <MisLinks token={token} />
              : <Navigate to="/" replace />
          }
        />
        <Route
          path="/utilidades-tickets"
          element={
            user && (roleId === 1 || roleId === 2 || roleId === 3)
              ? <Tickets token={token} />
              : <Navigate to="/" replace />
          }
        />
        <Route
          path="/utilidades-noticias"
          element={
            user && (roleId === 1 || roleId === 2 || roleId === 3)
              ? <Noticias token={token} />
              : <Navigate to="/" replace />
          }
        />
        <Route
          path="/conversaciones/:convId/ver"
          element={
            user && (roleId === 1 || roleId === 2 || roleId === 3)
              ? <ConversationViewer token={token} />
              : <Navigate to="/" replace />
          }
        />
        <Route path="/pago/resultado" element={<PagoResultado />} />
        <Route path="/p/:slug" element={<PublicPortal />} />
        <Route path="/p/:slug/:section" element={<PublicPortal />} />
        <Route path="/tienda/:slug" element={<Storefront />} />
        <Route path="/cita/:slug" element={<PublicCita />} />
        <Route path="/ticket/:slug" element={<PublicTicket />} />
        <Route path="/news/:slug" element={<PublicNews />} />
        <Route
          path="/recargar"
          element={
            user
              ? <Mora username={user} onLogout={handleLogout} allowBack />
              : <Navigate to="/" replace />
          }
        />
      </Routes>
    );
  };

  // Ruta "limpia" del widget de chat embebible: se sirve dentro de un iframe en
  // páginas de terceros, así que NO debe llevar Navbar, widgets ni gating de auth.
  if (window.location.pathname.startsWith('/iframe/')) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/iframe/:companyId/:channelId" element={<WebChatPage />} />
        </Routes>
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <div style={{ minHeight: '100vh', backgroundColor: theme.colors.background, color: theme.colors.text, fontFamily: 'system-ui, sans-serif' }}>
        <Navbar
          user={user}
          roleId={roleId}
          perUserCalendar={perUserCalendar}
          onLogout={handleLogout}
        />
        {renderContent()}
        {showWidget && <AdminNotifier token={token} />}
        {showWidget && <SaldoWidget saldo={companySaldo} />}
        <NotificationModal
          notifications={notifications}
          onClose={() => setNotifications([])}
        />
      </div>
    </BrowserRouter>
  );
}

export default App;
